import React, { useContext, useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  CssBaseline,
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  Avatar,
  Chip,
  Stack,
  Divider,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input/input";
import { useNavigate } from "react-router-dom";
import UserProfileContext from "../../context/userProfile/UserProfileContext";
import UserDataAPI from "../../api/firebase/UserDataAPI";
import AuthAPI from "../../api/firebase/AuthAPI";
import CustomisedSnackbar from "../../components/common/Snackbar/CustomisedSnackbar";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";

const UserProfilePage = () => {
  const navigate = useNavigate();
  const userProfileContext = useContext(UserProfileContext);
  const userDataApi = useMemo(() => new UserDataAPI(), []);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [formData, setFormData] = useState({
    displayName: "",
    email: "",
    mobile: "",
  });

  const [initialData, setInitialData] = useState({
    displayName: "",
    email: "",
    mobile: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (userProfileContext.state !== null) {
      const u = userProfileContext.state;
      setUser(u);
      const data = {
        displayName: u.displayName || u.name || "",
        email: u.email || "",
        mobile: u.mobile || "",
      };
      setFormData(data);
      setInitialData(data);
    }
  }, [userProfileContext.state]);

  const isDirty = useMemo(() => {
    return (
      formData.displayName !== initialData.displayName ||
      formData.email !== initialData.email ||
      formData.mobile !== initialData.mobile
    );
  }, [formData, initialData]);

  const getInitials = (name) => {
    if (!name || typeof name !== "string") return "U";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.displayName || formData.displayName.trim().length < 2) {
      newErrors.displayName = "Please enter your full name (at least 2 characters)";
    }
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = "Please enter a valid email address";
    }
    const cleanMobile = formData.mobile ? formData.mobile.replace(/\D/g, "") : "";
    if (formData.mobile && formData.mobile.trim() !== "") {
      if (
        !isValidPhoneNumber(formData.mobile) &&
        !/^[6-9]\d{9}$/.test(cleanMobile.slice(-10))
      ) {
        newErrors.mobile = "Please enter a valid 10-digit mobile number";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveButtonClick = () => {
    if (!validate()) return;
    setLoading(true);

    const userId = user?.id || user?.uid;
    const payload = {
      displayName: formData.displayName.trim(),
      email: formData.email.trim(),
      mobile: formData.mobile.trim(),
    };

    const data = {
      payload,
      id: userId,
    };

    userDataApi
      .addUserData(data)
      .then((res) => {
        if (res && res.success) {
          userProfileContext.setState({ ...user, ...payload });
          setInitialData({ ...formData });
          setSnackbarSeverity("success");
          setSnackbarMessage("Profile details updated successfully!");
          setOpenSnackbar(true);
        } else {
          setSnackbarSeverity("error");
          setSnackbarMessage("Failed to update profile. Please try again.");
          setOpenSnackbar(true);
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setSnackbarSeverity("error");
        setSnackbarMessage("An error occurred while updating profile.");
        setOpenSnackbar(true);
        console.error("Profile update error:", err);
      });
  };

  const handleCancelButtonClick = () => {
    setFormData({ ...initialData });
    setErrors({});
  };

  const handleLogout = async () => {
    try {
      const authAPI = new AuthAPI();
      await authAPI.signoutUser();
      userProfileContext.setState(null);
      localStorage.clear();
      navigate("/login");
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  const ordersCount = Array.isArray(user?.orders) ? user.orders.length : 0;
  const addressesCount = Array.isArray(user?.addresses) ? user.addresses.length : 0;
  const wishlistCount = Array.isArray(user?.likedItems) ? user.likedItems.length : 0;

  return (
    <Container component="main" maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <CssBaseline />

      {/* Page Title & Breadcrumb header */}
      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: "#0f172a",
            letterSpacing: "-0.02em",
            fontSize: { xs: "1.65rem", sm: "2rem" },
          }}
        >
          My Account
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          Manage your personal details, order history, and delivery preferences
        </Typography>
      </Box>

      <Grid container spacing={{ xs: 2.5, md: 3.5 }}>
        {/* Left Column: Profile Card & Hub Shortcuts */}
        <Grid item xs={12} md={4}>
          <Stack spacing={2.5}>
            {/* Identity Card */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: "20px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 10px 30px -10px rgba(15, 23, 42, 0.05)",
                textAlign: "center",
                bgcolor: "#ffffff",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Subtle top brand accent line */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  background: "linear-gradient(90deg, #2f5d7e, #059669)",
                }}
              />

              {/* Avatar with gradient border */}
              <Box
                sx={{
                  display: "inline-flex",
                  p: 0.5,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, rgba(47, 93, 126, 0.2), rgba(5, 150, 105, 0.2))",
                  mb: 2,
                }}
              >
                <Avatar
                  sx={{
                    width: 76,
                    height: 76,
                    bgcolor: "primary.main",
                    fontSize: "1.75rem",
                    fontWeight: 800,
                    color: "#ffffff",
                    boxShadow: "0 4px 14px rgba(47, 93, 126, 0.25)",
                  }}
                >
                  {getInitials(formData.displayName || user?.email)}
                </Avatar>
              </Box>

              {/* Display Name & Verified Status */}
              <Typography
                variant="h6"
                sx={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.2, mb: 0.5 }}
              >
                {formData.displayName || "Ayurvedic Patron"}
              </Typography>

              <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.85rem", mb: 1.5 }}>
                {formData.email}
              </Typography>

              <Chip
                icon={<CheckCircleRoundedIcon sx={{ fontSize: "16px !important", color: "#16a34a !important" }} />}
                label="Verified Member"
                size="small"
                sx={{
                  bgcolor: "#f0fdf4",
                  color: "#15803d",
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  borderRadius: "6px",
                  border: "1px solid #bbf7d0",
                  mb: 2.5,
                }}
              />

              <Divider sx={{ borderColor: "#f1f5f9", mb: 2 }} />

              {/* Quick Stat Counters */}
              <Grid container spacing={1} sx={{ textAlign: "center" }}>
                <Grid item xs={4}>
                  <Box
                    onClick={() => navigate("/userOrders")}
                    sx={{
                      p: 1,
                      borderRadius: "10px",
                      cursor: "pointer",
                      "&:hover": { bgcolor: "#f8fafc" },
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "primary.main", lineHeight: 1.1 }}>
                      {ordersCount}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                      Orders
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={4}>
                  <Box
                    onClick={() => navigate("/userAddresses")}
                    sx={{
                      p: 1,
                      borderRadius: "10px",
                      cursor: "pointer",
                      "&:hover": { bgcolor: "#f8fafc" },
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#059669", lineHeight: 1.1 }}>
                      {addressesCount}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                      Addresses
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={4}>
                  <Box
                    onClick={() => navigate("/likedItems")}
                    sx={{
                      p: 1,
                      borderRadius: "10px",
                      cursor: "pointer",
                      "&:hover": { bgcolor: "#f8fafc" },
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#e11d48", lineHeight: 1.1 }}>
                      {wishlistCount}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                      Wishlist
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Account Navigation Shortcuts */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: "20px",
                border: "1px solid #e2e8f0",
                bgcolor: "#ffffff",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: "#64748b",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  display: "block",
                  px: 1,
                  mb: 1.5,
                }}
              >
                Account Quick Links
              </Typography>

              <Stack spacing={1}>
                {[
                  {
                    title: "My Orders",
                    subtitle: "View order history & track items",
                    icon: <ShoppingBagOutlinedIcon sx={{ color: "primary.main", fontSize: 20 }} />,
                    path: "/userOrders",
                    count: ordersCount,
                  },
                  {
                    title: "Delivery Addresses",
                    subtitle: "Manage saved shipping locations",
                    icon: <LocationOnOutlinedIcon sx={{ color: "#059669", fontSize: 20 }} />,
                    path: "/userAddresses",
                    count: addressesCount,
                  },
                  {
                    title: "My Wishlist",
                    subtitle: "Saved Ayurvedic favorites",
                    icon: <FavoriteBorderRoundedIcon sx={{ color: "#e11d48", fontSize: 20 }} />,
                    path: "/likedItems",
                    count: wishlistCount,
                  },
                ].map((item) => (
                  <Box
                    key={item.title}
                    onClick={() => navigate(item.path)}
                    sx={{
                      p: 1.5,
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      border: "1px solid transparent",
                      "&:hover": {
                        bgcolor: "#f8fafc",
                        borderColor: "#e2e8f0",
                        transform: "translateX(3px)",
                      },
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Box
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: "10px",
                          bgcolor: "#f8fafc",
                          border: "1px solid #f1f5f9",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700, color: "#1e293b", fontSize: "0.88rem" }}
                        >
                          {item.title}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.72rem" }}>
                          {item.subtitle}
                        </Typography>
                      </Box>
                    </Stack>
                    <ArrowForwardRoundedIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                  </Box>
                ))}
              </Stack>

              <Divider sx={{ my: 1.5, borderColor: "#f1f5f9" }} />

              {/* Sign Out Button */}
              <Button
                variant="text"
                fullWidth
                onClick={handleLogout}
                startIcon={<LogoutRoundedIcon sx={{ fontSize: 18 }} />}
                sx={{
                  color: "#dc2626",
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: "10px",
                  py: 1,
                  fontSize: "0.85rem",
                  "&:hover": { bgcolor: "#fee2e2" },
                }}
              >
                Sign Out of Account
              </Button>
            </Paper>
          </Stack>
        </Grid>

        {/* Right Column: Personal Details & Security Form */}
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            {/* Edit Personal Details Form */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 4 },
                borderRadius: "20px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 10px 30px -10px rgba(15, 23, 42, 0.05)",
                bgcolor: "#ffffff",
              }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, color: "#0f172a", fontSize: "1.15rem" }}
                >
                  Personal Details
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.78rem" }}>
                  Keep your personal and contact details updated for accurate receipts and order notifications
                </Typography>
              </Box>

              <Grid container spacing={2.5}>
                {/* Full Name */}
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, color: "#475569", display: "block", mb: 0.75 }}
                  >
                    Full Name *
                  </Typography>
                  <TextField
                    required
                    fullWidth
                    name="displayName"
                    value={formData.displayName}
                    onChange={(e) => handleFieldChange("displayName", e.target.value)}
                    error={Boolean(errors.displayName)}
                    helperText={errors.displayName}
                    placeholder="e.g. Rahul Sharma"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutlineRoundedIcon
                            sx={{ fontSize: 20, color: "text.secondary" }}
                          />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: "10px" },
                    }}
                  />
                </Grid>

                {/* Mobile Number */}
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, color: "#475569", display: "block", mb: 0.75 }}
                  >
                    Mobile Number
                  </Typography>
                  <Box>
                    <PhoneInput
                      placeholder="10-digit mobile number"
                      defaultCountry="IN"
                      value={formData.mobile}
                      onChange={(val) => handleFieldChange("mobile", val || "")}
                      className={`modern-phone-input ${errors.mobile ? "has-error" : ""}`}
                      maxLength={15}
                    />
                    {errors.mobile && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#d32f2f",
                          fontSize: "0.72rem",
                          mt: 0.5,
                          display: "block",
                          ml: 1,
                        }}
                      >
                        {errors.mobile}
                      </Typography>
                    )}
                  </Box>
                </Grid>

                {/* Email Address */}
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: "#475569", display: "block" }}
                    >
                      Email Address *
                    </Typography>
                    <Chip
                      icon={<VerifiedUserOutlinedIcon sx={{ fontSize: "14px !important", color: "#16a34a !important" }} />}
                      label="Primary Email"
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        bgcolor: "#f0fdf4",
                        color: "#15803d",
                        borderRadius: "4px",
                      }}
                    />
                  </Box>
                  <TextField
                    required
                    fullWidth
                    name="email"
                    value={formData.email}
                    onChange={(e) => handleFieldChange("email", e.target.value)}
                    error={Boolean(errors.email)}
                    helperText={errors.email}
                    placeholder="e.g. rahul@example.com"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlinedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: "10px" },
                    }}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3.5, borderColor: "#f1f5f9" }} />

              {/* Form Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
                  {isDirty ? "⚠️ You have unsaved changes" : "All changes are up to date"}
                </Typography>

                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="outlined"
                    onClick={handleCancelButtonClick}
                    disabled={!isDirty || loading}
                    sx={{
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 700,
                      borderColor: "#cbd5e1",
                      color: "#475569",
                      py: 1,
                      px: 2.5,
                      "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" },
                    }}
                  >
                    Reset
                  </Button>

                  <Button
                    variant="contained"
                    onClick={handleSaveButtonClick}
                    disabled={!isDirty || loading}
                    startIcon={
                      loading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <CheckRoundedIcon />
                      )
                    }
                    sx={{
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 800,
                      py: 1.1,
                      px: 3,
                      boxShadow: "0 4px 14px rgba(47, 93, 126, 0.3)",
                    }}
                  >
                    {loading ? "Saving Changes..." : "Save Changes"}
                  </Button>
                </Stack>
              </Box>
            </Paper>

            {/* Account Security & Trust Info Card */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: "20px",
                border: "1px solid #e2e8f0",
                bgcolor: "#ffffff",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 800, color: "#0f172a", mb: 2, display: "flex", alignItems: "center", gap: 1 }}
              >
                <ShieldOutlinedIcon sx={{ fontSize: 20, color: "primary.main" }} />
                Security & Data Privacy
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "12px",
                      bgcolor: "#f8fafc",
                      border: "1px solid #f1f5f9",
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: "#1e293b", fontSize: "0.85rem", mb: 0.5 }}
                    >
                      🔒 256-Bit SSL Encryption
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.4, display: "block" }}>
                      Your profile and account data is encrypted using high-grade security protocols to protect your privacy.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "12px",
                      bgcolor: "#f8fafc",
                      border: "1px solid #f1f5f9",
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: "#1e293b", fontSize: "0.85rem", mb: 0.5 }}
                    >
                      🌿 Ayurvedic Support
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.4, display: "block" }}>
                      Need help managing your profile or account? Reach our dedicated wellness team at{" "}
                      <Box
                        component="a"
                        href="mailto:support@inproveda.com"
                        sx={{
                          color: "primary.main",
                          textDecoration: "none",
                          fontWeight: 600,
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        support@inproveda.com
                      </Box>
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      {/* Notifications Toast */}
      <CustomisedSnackbar
        open={openSnackbar}
        handleClose={() => setOpenSnackbar(false)}
        title="Profile Updated"
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Container>
  );
};

export default UserProfilePage;
